import React, { useState, useEffect } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import css from './FieldPhoneInput.module.css';

/**
 * Actual phone input component used by React Final Form
 */
const FieldPhoneInputComponent = props => {
  const {
    input,
    meta,
    label,
    placeholder,
    className,
    defaultCountry = 'US',
    ...rest
  } = props;

  const [PhoneInput, setPhoneInput] = useState(null);

  useEffect(() => {
    import('react-phone-number-input').then(mod => {
      require('react-phone-number-input/style.css');
      setPhoneInput(() => mod.default);
    });
  }, []);

  const { touched, error, active } = meta;
  const hasError = (touched || !active) && error;

  if (!PhoneInput) return null;

  return (
    <div className={classNames(css.root, className)}>
      {label && <label className={css.label}>{label}</label>}

      <PhoneInput
        {...rest}
        value={input.value}
        onChange={input.onChange}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
        defaultCountry={defaultCountry}
        placeholder={placeholder}
        international
        className={classNames(css.input, {
          [css.error]: hasError,
        })}
      />

      {hasError && <span className={css.errorMessage}>{error}</span>}
    </div>
  );
};

/**
 * Wrapper used in forms
 * Validates phone number based on the selected country using the library's built-in validation
 */
const FieldPhoneInput = props => {
  const { validate: customValidate } = props;

  // Compose library validation with custom validators passed from form
  const validate = async value => {
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return undefined;
    }

    if (customValidate) {
      const customError = typeof customValidate === 'function' ? customValidate(value) : undefined;
      if (customError) return customError;
    }

    const { isValidPhoneNumber, isPossiblePhoneNumber } = await import('react-phone-number-input');

    if (!isPossiblePhoneNumber(value)) {
      return 'Phone number length is invalid for the selected country';
    }

    if (!isValidPhoneNumber(value)) {
      return 'Invalid phone number for the selected country';
    }

    return undefined;
  };

  return (
    <Field
      {...props}
      component={FieldPhoneInputComponent}
      validate={validate}
    />
  );
};

export default FieldPhoneInput;